import { z } from 'zod';

import { EMBLEMS, EXTRA_POLL, IDENTITY } from '../constants/identity';
import { SquadPollKindSchema, SquadPollStatusSchema } from '../enums';
import { IdSchema, IsoDateSchema } from './common';

/**
 * Идентичность отряда и опросы (BLUEPRINT Stage 12, задачи 1 и 4):
 * `squad.identity`, `squad.suggestName`, `squad.voteName`, `squad.setEmblem`,
 * `squad.proposeExtra`, `squad.votePoll`.
 */

export const EmblemSchema = z.enum(EMBLEMS);
export type EmblemCode = z.infer<typeof EmblemSchema>;

export const SquadNameSchema = z
  .string()
  .trim()
  .min(IDENTITY.nameMinLength, `Название от ${IDENTITY.nameMinLength} символов`)
  .max(IDENTITY.nameMaxLength, `Название до ${IDENTITY.nameMaxLength} символов`);

export const PollOptionPublicSchema = z.object({
  value: z.string(),
  label: z.string(),
  /** Кто предложил вариант; null — вариант от системы */
  byUserId: IdSchema.nullable(),
  votes: z.number().int().nonnegative(),
  mine: z.boolean(),
});
export type PollOptionPublic = z.infer<typeof PollOptionPublicSchema>;

export const SquadPollPublicSchema = z.object({
  id: IdSchema,
  squadId: IdSchema,
  kind: SquadPollKindSchema,
  status: SquadPollStatusSchema,
  options: z.array(PollOptionPublicSchema),
  /** Сколько «да» нужно доп. сбору (для опроса о названии — 0) */
  threshold: z.number().int().nonnegative(),
  closesAt: z.string().datetime(),
  closedAt: z.string().datetime().nullable(),
  resultValue: z.string().nullable(),
  /** Созданный доп. сбор, если опрос набрал порог */
  gatheringId: IdSchema.nullable(),
  /** Активность доп. сбора */
  activityId: IdSchema.nullable(),
  activityName: z.string().nullable(),
  /** Мой голос (значение варианта) */
  myVote: z.string().nullable(),
  /** Могу ли я ещё предложить вариант названия */
  canSuggest: z.boolean(),
  votersTotal: z.number().int().nonnegative(),
});
export type SquadPollPublic = z.infer<typeof SquadPollPublicSchema>;

/** `squad.identity`: название, эмблема, опрос названия и активные опросы доп. сбора. */
export const SquadIdentitySchema = z.object({
  squadId: IdSchema,
  /**
   * Таймзона города отряда. Даты опроса доп. сбора клиент обязан собирать именно в ней: сервер
   * переводит присланную дату в UTC в этой зоне, и если клиент возьмёт дату в зоне устройства
   * или в UTC, опрос уедет на сутки.
   */
  timezone: z.string(),
  name: z.string().nullable(),
  emblem: EmblemSchema.nullable(),
  /** Сколько сборов уже проведено (по ним открывается опрос названия) */
  gatheringsDone: z.number().int().nonnegative(),
  /** Серия сборов подряд полным составом */
  fullRosterStreak: z.number().int().nonnegative(),
  /** Опрос названия: активный или последний закрытый; null — ещё рано */
  namePoll: SquadPollPublicSchema.nullable(),
  /** Открытые опросы доп. сбора */
  extraPolls: z.array(SquadPollPublicSchema),
  /**
   * Группа отряда привязана (`/bind`). Опрос доп. сбора идёт в группу (BLUEPRINT 2.2.6); без неё
   * его никто не увидит, поэтому запуск закрыт с причиной `group_required` (раунд 3, C1).
   */
  hasGroup: z.boolean(),
  /** Эмблему меняет капитан */
  canSetEmblem: z.boolean(),
  /** Опрос названия уже можно запускать (2 сбора проведено, названия нет) */
  canStartNamePoll: z.boolean(),
  emblems: z.array(EmblemSchema),
});
export type SquadIdentity = z.infer<typeof SquadIdentitySchema>;

const SquadIdInput = z.object({ squadId: IdSchema.optional() });

export const SquadSuggestNameInputSchema = SquadIdInput.extend({ name: SquadNameSchema });
export type SquadSuggestNameInput = z.infer<typeof SquadSuggestNameInputSchema>;

export const SquadVoteInputSchema = z.object({ pollId: IdSchema, value: z.string().trim().min(1).max(64) });
export type SquadVoteInput = z.infer<typeof SquadVoteInputSchema>;

export const SquadSetEmblemInputSchema = SquadIdInput.extend({ emblem: EmblemSchema });
export type SquadSetEmblemInput = z.infer<typeof SquadSetEmblemInputSchema>;

/** `squad.proposeExtra` (2.2.6): активность и до трёх дат; «да» от четверых создаёт сбор. */
export const SquadProposeExtraInputSchema = SquadIdInput.extend({
  activityId: IdSchema,
  dates: z
    .array(IsoDateSchema)
    .min(1, 'Предложи хотя бы одну дату')
    .max(EXTRA_POLL.maxDateOptions, `Не больше ${EXTRA_POLL.maxDateOptions} дат`),
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Время в формате ЧЧ:ММ'),
});
export type SquadProposeExtraInput = z.infer<typeof SquadProposeExtraInputSchema>;

/**
 * «Поделиться сбором» (Stage 12, задача 6): `share.gathering` готовит карточку.
 * `preparedMessageId` есть — клиент зовёт `shareMessage(id)`, иначе делится ссылкой (`shareURL`).
 */
export const ShareCardSchema = z.object({
  preparedMessageId: z.string().nullable(),
  text: z.string(),
  url: z.string(),
});
export type ShareCard = z.infer<typeof ShareCardSchema>;

export const ShareGatheringInputSchema = z.object({ gatheringId: IdSchema });
export type ShareGatheringInput = z.infer<typeof ShareGatheringInputSchema>;
